import { api } from '../../shared/api/client'

export interface SearchResult {
  entityType: 'design' | 'product' | 'wb_product' | 'ozon_product' | 'ym_product' | 'category' | 'activity_log';
  id: string | number;
  title: string;
  subtitle?: string;
  preview?: string;
  category?: string;
  status?: string;
  seller?: string;
  timestamp?: string;
  matchedField: string;
  matchedValue: string;
  url: string;
}

export interface GlobalSearchResponse {
  total: number;
  results: SearchResult[];
  byType: {
    designs: number;
    products: number;
    wb_products: number;
    ozon_products: number;
    ym_products: number;
    categories: number;
    activity_logs: number;
  };
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
  suggestion?: string | null;
}

export interface SearchFilters {
  categories?: string[];
  status?: string[];
  dateFrom?: string;
  dateTo?: string;
  userId?: number;
  designCode?: number | number[];
  themeNumber?: number | number[];
  designType?: string[];
  brand?: string[];
  gender?: string[];
  age?: string[];
  seasonal?: number[];
  theme?: string[];
  emotion?: string[];
  language?: string[];
  occasion?: string[];
  designTypeCategory?: string[];
  imageText?: string[];
  imageSubject?: string[];
  profession?: string[];
  ageOnImage?: number[];
  holidayDate?: string;
  article?: string[];
  barcode?: string[];
  title?: string[];
  productBrand?: string[];
  productCategory?: string[];
  nmId?: number | number[];
  vendorCode?: string[];
  sellerId?: number[];
}

export interface SearchOptions {
  limit?: number;
  offset?: number;
  sortBy?: 'relevance' | 'name' | 'date' | 'updated';
  sortOrder?: 'ASC' | 'DESC';
  filters?: SearchFilters;
}

export interface PaginatedSearchResponse {
  items: SearchResult[];
  total: number;
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
}

export const searchAPI = {
  globalSearch: async (query: string, options?: SearchOptions): Promise<GlobalSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.filters && Object.keys(options.filters).length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    const response = await api.get(`/search?${params.toString()}`);
    const data = response.data?.data || response.data || { total: 0, results: [], byType: {} };
    return {
      total: data.total || 0,
      results: data.results || [],
      byType: data.byType || {},
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },

  searchDesigns: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.filters && Object.keys(options.filters).length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    const response = await api.get(`/search/designs?${params.toString()}`);
    const data = response.data?.data || response.data || { items: [], total: 0 };
    return {
      items: data.results || data.items || [],
      total: data.total || 0,
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },

  searchProducts: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.filters && Object.keys(options.filters).length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    const response = await api.get(`/search/products?${params.toString()}`);
    const data = response.data?.data || response.data || { items: [], total: 0 };
    return {
      items: data.results || data.items || [],
      total: data.total || 0,
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },

  searchWbProducts: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.filters && Object.keys(options.filters).length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    const response = await api.get(`/search/wb-products?${params.toString()}`);
    const data = response.data?.data || response.data || { items: [], total: 0 };
    return {
      items: data.results || data.items || [],
      total: data.total || 0,
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },

  searchOzonProducts: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.filters && Object.keys(options.filters).length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    const response = await api.get(`/search/ozon-products?${params.toString()}`);
    const data = response.data?.data || response.data || { items: [], total: 0 };
    return {
      items: data.results || data.items || [],
      total: data.total || 0,
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },

  /**
   * Поиск по товарам Я.Маркета.
   *
   * У search-service нет своего индекса по `ym_products` (в отличие от
   * `/search/wb-products`/`/search/ozon-products`, которые читают зеркала
   * площадок напрямую в этом сервисе) — заводить его было бы бэкенд-правкой.
   * Вместо нового маршрута ищем как сама вкладка Маркета ищет по своим
   * кабинетам: через существующий клиент `features/yandex-market/api.ts`
   * (`GET /yandex-market/sellers/:sellerId/products?search=…`), один запрос
   * на активный кабинет Маркета, результаты — сложены в один список.
   */
  searchYmProducts: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const { yandexMarketAPI } = await import('../yandex-market/api');
    const { photoUrlOf, ymStatusLabel } = await import('../yandex-market/catalog/columns');

    const sellersResponse = await yandexMarketAPI.getSellers();
    const sellersData = sellersResponse.data?.data ?? sellersResponse.data ?? [];
    const sellers = Array.isArray(sellersData) ? sellersData : [];
    // Тот же отбор, что у страницы каталога Маркета (YandexMarketCatalogPage.tsx):
    // активный кабинет с сохранённым токеном.
    const activeSellers = sellers.filter((seller: any) => {
      const hasToken = Boolean(seller.has_token);
      return Boolean(seller.active || seller.is_active) && hasToken;
    });

    const limit = options?.limit || 20;
    const perSellerLimit = Math.min(limit, 20);

    const pages = await Promise.all(
      activeSellers.map((seller: any) => {
        const sellerId = Number(seller.seller_id ?? seller.id);
        return yandexMarketAPI
          .getProducts(sellerId, { search: query, lean: 1, limit: perSellerLimit })
          .then((response) => ({ seller, sellerId, response }))
          .catch(() => null);
      }),
    );

    const items: SearchResult[] = [];
    let total = 0;
    for (const page of pages) {
      if (!page) continue;
      const data = page.response.data?.data ?? page.response.data ?? {};
      const rows: any[] = Array.isArray(data.items) ? data.items : [];
      total += Number(data.total) || rows.length;
      for (const row of rows) {
        const title = row.name || row.offer_id || 'Без названия';
        items.push({
          entityType: 'ym_product',
          id: `${page.sellerId}:${row.offer_id}`,
          title,
          subtitle: row.offer_id ? `Артикул: ${row.offer_id}` : undefined,
          preview: photoUrlOf(row) || undefined,
          category: row.market_category_name || undefined,
          status: ymStatusLabel(row.card_status),
          seller: page.seller.seller_name,
          timestamp: row.updatedAt || row.updated_at || undefined,
          matchedField: 'name',
          matchedValue: title,
          url: `/yandex-market?offer=${encodeURIComponent(row.offer_id || '')}`,
        });
      }
    }

    return {
      items: items.slice(0, limit),
      total,
      hasMore: total > items.length,
    };
  },

  searchCategories: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

    const response = await api.get(`/search/categories?${params.toString()}`);
    const data = response.data?.data || response.data || { items: [], total: 0 };
    return {
      items: data.results || data.items || [],
      total: data.total || 0,
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },

  searchActivityLogs: async (query: string, options?: SearchOptions): Promise<PaginatedSearchResponse> => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.filters && Object.keys(options.filters).length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    const response = await api.get(`/search/activity-logs?${params.toString()}`);
    const data = response.data?.data || response.data || { items: [], total: 0 };
    return {
      items: data.results || data.items || [],
      total: data.total || 0,
      page: data.page,
      pageSize: data.pageSize,
      hasMore: data.hasMore,
    };
  },
}

// statusAPI
export interface ServiceStatus {
  service: string
  status: 'online' | 'offline' | 'error'
  message: string
  hasToken: boolean
  tokenValid: boolean
  lastCheck: string
  error?: string
}

const MICROSERVICES = [
  { name: 'Auth Service', serviceName: 'auth-service' },
  { name: 'User Service', serviceName: 'user-service' },
  { name: 'Product Service', serviceName: 'product-service' },
  { name: 'Category Service', serviceName: 'category-service' },
  { name: 'Design Service', serviceName: 'design-service' },
  { name: 'Seller Service', serviceName: 'seller-service' },
  { name: 'Activity Log Service', serviceName: 'activity-log-service' },
  { name: 'Notification Service', serviceName: 'notification-service' },
  { name: 'Analytics Service', serviceName: 'analytics-service' },
  { name: 'ETL Service', serviceName: 'etl-service' },
  { name: 'Wildberries', serviceName: 'wb-service' },
  { name: 'Ozon', serviceName: 'ozon-service' },
]

async function checkServiceHealth(serviceName: string, displayName: string): Promise<ServiceStatus> {
  try {
    const response = await api.post(`/health/${serviceName}`, {}, {
      timeout: 5000,
    })

    if (response.data?.success && response.data?.data?.status === 'ok') {
      return {
        service: displayName,
        status: 'online',
        message: `${displayName} is running and healthy`,
        hasToken: false,
        tokenValid: true,
        lastCheck: new Date().toISOString(),
      }
    } else {
      return {
        service: displayName,
        status: 'error',
        message: `${displayName} returned unexpected response`,
        hasToken: false,
        tokenValid: false,
        lastCheck: new Date().toISOString(),
        error: 'Unexpected response format',
      }
    }
  } catch (error: any) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        service: displayName,
        status: 'offline',
        message: `${displayName} is not responding (timeout)`,
        hasToken: false,
        tokenValid: false,
        lastCheck: new Date().toISOString(),
        error: 'Request timeout',
      }
    }

    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return {
        service: displayName,
        status: 'offline',
        message: `${displayName} is not reachable`,
        hasToken: false,
        tokenValid: false,
        lastCheck: new Date().toISOString(),
        error: 'Network error',
      }
    }

    if (error.response?.data?.error) {
      return {
        service: displayName,
        status: 'error',
        message: `${displayName} returned an error`,
        hasToken: false,
        tokenValid: false,
        lastCheck: new Date().toISOString(),
        error: error.response.data.error.message || 'Service error',
      }
    }

    return {
      service: displayName,
      status: 'offline',
      message: `${displayName} is not responding`,
      hasToken: false,
      tokenValid: false,
      lastCheck: new Date().toISOString(),
      error: error.message || 'Unknown error',
    }
  }
}

export const statusAPI = {
  checkBackend: async (): Promise<ServiceStatus> => {
    try {
      const response = await api.get('/health/detailed', { timeout: 5000 })
      const data = response.data
      const dbStatus = data.database?.connected ? 'Connected' : 'Disconnected'
      return {
        service: 'Backend Gateway',
        status: 'online',
        message: `Backend gateway is running (uptime: ${Math.round(data.uptime || 0)}s, DB: ${dbStatus})`,
        hasToken: false,
        tokenValid: true,
        lastCheck: new Date().toISOString(),
      }
    } catch (error: any) {
      return {
        service: 'Backend Gateway',
        status: 'offline',
        message: 'Backend gateway is not responding',
        hasToken: false,
        tokenValid: false,
        lastCheck: new Date().toISOString(),
        error: error.message || 'Connection failed',
      }
    }
  },

  checkAllServices: async (): Promise<ServiceStatus[]> => {
    const backendCheck = statusAPI.checkBackend()
    const serviceChecks = MICROSERVICES.map(({ name, serviceName }) =>
      checkServiceHealth(serviceName, name)
    )

    const results = await Promise.allSettled([
      backendCheck,
      ...serviceChecks,
    ])

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value
      } else {
        if (index === 0) {
          return {
            service: 'Backend Gateway',
            status: 'error' as const,
            message: 'Failed to check backend gateway status',
            hasToken: false,
            tokenValid: false,
            lastCheck: new Date().toISOString(),
            error: result.reason?.message || 'Unknown error',
          }
        }
        const serviceIndex = index - 1
        const { name } = MICROSERVICES[serviceIndex]
        return {
          service: name,
          status: 'error' as const,
          message: `Failed to check ${name} status`,
          hasToken: false,
          tokenValid: false,
          lastCheck: new Date().toISOString(),
          error: result.reason?.message || 'Unknown error',
        }
      }
    })
  },
}
